import { MaterialTableProps } from 'material-table';
import React, { useState } from 'react';
import FncLabel from '../../../../../components/Forms/FncLabel/FncLabel';
import TransitionModal from '../../../../../components/Modal/TransitionModal';
import TableWithEdit from '../../../../../components/Tables/TableWithEdit';
import { tFormKey, tKey } from '../../../../../helpers/translate';
import { InvoiceData, InvoiceListItem } from '../../../../../redux/accounting/definitions';
import styles from './invoicesActionsModal.module.scss';
import { formatNumber } from '../../../../../helpers/formatNumber';
import { getInvoiceFromApi } from '../../../../../api/accounting';
import errorMessage from '../../../../../helpers/errorMessage';

interface InvoicesActionsModalProps {
  show: boolean;
  invoices: InvoiceListItem[];
  actionName: string;
  invoiceTransition: (invoiceData: InvoiceData) => Promise<void>;
  handleClose: () => void;
  handleInvoicesDone: (processedInvoices: number[]) => void;
}

export type InfoMessage = {
  type: 'error' | 'info';
  content: string;
};

const InvoicesActionsModal: React.FC<InvoicesActionsModalProps> = ({
  show,
  invoices,
  actionName,
  invoiceTransition,
  handleClose,
  handleInvoicesDone,
}) => {
  const [exit, setExit] = useState<boolean>(false);
  const [infoMessages, setInfoMessages] = useState<InfoMessage[]>([]);
  const [processedInvoices, setProcessedInvoices] = useState<number[]>([]);
  const tableData: MaterialTableProps<InvoiceListItem> = {
    columns: [
      {
        field: 'reference_number',
        title: tKey('Factura'),
        //@ts-ignore
        width: '110px',
        maxWidth: '110px',
      },
      {
        field: 'entity_name',
        title: tKey('Proveedor'),
        //@ts-ignore
        width: '200px',
      },
      {
        field: 'invoice_date',
        title: tFormKey('Fecha'),
        headerStyle: {
          textAlign: 'center',
        },
        cellStyle: {
          textAlign: 'center',
        },
        render: (rowData: InvoiceListItem) => <div style={{ textAlign: 'center' }}>{rowData.invoice_date}</div>,
        //@ts-ignore
        width: '120px',
      },
      {
        field: 'total_amount',
        title: tKey('Total'),
        type: 'numeric',
        render: (rowData: InvoiceListItem) => <div>{formatNumber({ value: rowData.total_amount }) + ' €'}</div>,
        //@ts-ignore
        width: '110px',
      },
      {
        field: 'center_costs',
        title: tKey('Centro de coste'),
        //@ts-ignore
        width: '220px',
      },
      {
        field: 'account_number',
        title: tKey('IBAN'),
        headerStyle: { width: '180px', maxWidth: '180px' },
        //@ts-ignore
        width: '220px',
      },
    ],
    options: {
      tableLayout: 'auto',
      paging: false,
      showTextRowsSelected: false,
      showTitle: false,
      maxBodyHeight: '400px',
    },
    data: invoices,
  };

  const titleText = (): string => {
    const paymentsCount = invoices.length;
    return `${actionName} -  ${paymentsCount} ${paymentsCount === 1 ? tKey('Factura') : tKey('Facturas')}`;
  };

  const doTransition = async (invoice: InvoiceListItem) => {
    const message = `Factura: ${invoice.reference_number}`;
    try {
      const invoiceData = await getInvoiceFromApi(invoice.id);
      await invoiceTransition(invoiceData);

      const messageOk = `${message}: procesada correctamente`;
      setInfoMessages(prevMessages => [...prevMessages, { type: 'info', content: messageOk }]);
      setProcessedInvoices(prevInvoices => [...prevInvoices, invoiceData.id]);
    } catch (error) {
      const messageKo = `${message} con error: ${errorMessage(error)}`;
      setInfoMessages(prevMessages => [...prevMessages, { type: 'error', content: messageKo }]);
    }
  };

  const doAction = async (): Promise<void> => {
    await Promise.all(invoices.map((invoice: InvoiceListItem) => doTransition(invoice)));
    setExit(true);
  };

  const handleYes = (): void => {
    exit ? handleInvoicesDone(processedInvoices) : doAction();
  };

  const showMessages = (): JSX.Element => {
    const ulName = 'result_list';
    const messages = infoMessages.map((infoMessage: InfoMessage, index) => (
      <li key={index} className={`${styles.list_item} ${styles['list_'.concat(infoMessage.type)]}`}>
        {infoMessage.content}
      </li>
    ));
    return (
      <div className={styles.message_area}>
        <FncLabel className={styles.message_label} htmlFor={ulName}>
          {tKey('Resultado')}
        </FncLabel>
        <ul id={ulName} className={styles.message_list}>
          {messages}
        </ul>
      </div>
    );
  };
  return (
    <TransitionModal
      view={show}
      handleClose={handleClose}
      title={titleText()}
      buttonOk={exit ? tKey('Salir') : actionName}
      buttonKo={exit ? undefined : tKey('Cancelar')}
      handleYes={handleYes}
      handleNo={handleClose}
    >
      <div className={styles.modal_content}>
        <TableWithEdit
          noSearch
          customContainer
          tableData={{ ...tableData }}
          key={JSON.stringify(tableData.data)}
          permission={true}
        />
        {showMessages()}
      </div>
    </TransitionModal>
  );
};

export default InvoicesActionsModal;
